import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, withRetry } from "@/lib/api";
import { LEVELS } from "@/lib/constants";
import { GraduationCap, Mail, Lock, User, GraduationCap as LevelIcon } from "lucide-react";
import { toast } from "sonner";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", level: "Seconde" });
  const [accountType, setAccountType] = useState("eleve");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await withRetry(() => login(form.email, form.password), 4, 3000);
        toast.success("Content de te revoir !");
      } else {
        const data = await withRetry(() => register(form.name, form.email, form.password, form.level, accountType), 4, 3000);
        toast.success("Bienvenue sur ProEdu !");
        navigate(data.role === "parent" ? "/parent" : "/dashboard");
        setLoading(false);
        return;
      }
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-violet-50 flex items-center justify-center p-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8" data-testid="auth-logo">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-500 flex items-center justify-center">
            <GraduationCap className="h-5 w-5 text-white" />
          </div>
          <span className="font-display font-bold text-2xl tracking-tight text-slate-900">ProEdu</span>
        </Link>

        <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/50 p-8">
          <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-full mb-8">
            <button
              onClick={() => { setMode("login"); setError(""); }}
              data-testid="auth-tab-login"
              className={`py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 ${mode === "login" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500"}`}
            >
              Connexion
            </button>
            <button
              onClick={() => { setMode("register"); setError(""); }}
              data-testid="auth-tab-register"
              className={`py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 ${mode === "register" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500"}`}
            >
              Inscription
            </button>
          </div>

          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">
            {mode === "login" ? "Ravi de te revoir" : "Crée ton compte gratuit"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {mode === "login" ? "Retrouve tes devoirs, tes fiches et ta progression." : "30 secondes suffisent pour s'organiser."}
          </p>

          {error && (
            <div className="mt-4 px-4 py-3 rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-700" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Prénom</label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={set("name")}
                    data-testid="auth-name-input"
                    placeholder="Ton prénom"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400"
                  />
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  data-testid="auth-email-input"
                  placeholder="toi@exemple.fr"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={set("password")}
                  data-testid="auth-password-input"
                  placeholder="6 caractères minimum"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400"
                />
              </div>
            </div>
            {mode === "register" && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Je suis…</label>
                <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-2xl">
                  <button
                    type="button"
                    onClick={() => setAccountType("eleve")}
                    data-testid="account-type-eleve"
                    className={`py-2.5 rounded-xl text-sm font-semibold transition-colors duration-200 ${accountType === "eleve" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500"}`}
                  >
                    Élève
                  </button>
                  <button
                    type="button"
                    onClick={() => setAccountType("parent")}
                    data-testid="account-type-parent"
                    className={`py-2.5 rounded-xl text-sm font-semibold transition-colors duration-200 ${accountType === "parent" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500"}`}
                  >
                    Parent
                  </button>
                </div>
              </div>
            )}
            {mode === "register" && accountType === "eleve" && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Niveau scolaire</label>
                <div className="relative">
                  <LevelIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <select
                    value={form.level}
                    onChange={set("level")}
                    data-testid="auth-level-select"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400"
                  >
                    {LEVELS.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-button"
              className="w-full py-3.5 rounded-full bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-60"
            >
              {loading ? "Connexion en cours…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </button>
            {loading && (
              <p className="text-xs text-center text-slate-400 animate-fade-in" data-testid="auth-loading-hint">
                Si le serveur se réveille, la connexion peut prendre jusqu'à 30 secondes — ne quitte pas la page.
              </p>
            )}
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-slate-400">ou</span></div>
          </div>
          <button
            type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            data-testid="google-login-button"
            className="w-full py-3.5 rounded-full bg-white border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 hover:border-slate-300 transition-colors duration-200 flex items-center justify-center gap-2.5"
          >
            <svg className="h-4.5 w-4.5 h-[18px] w-[18px]" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continuer avec Google
          </button>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">
          En créant un compte, tu acceptes de devenir un élève ultra-organisé.
        </p>
      </div>
    </div>
  );
}
